import React, { useMemo } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { FaTimes as X, FaSyncAlt as Refresh } from 'react-icons/fa'
import { useQueryClient } from '@tanstack/react-query'
import {
  useSequencerProjects,
  type ProjectListEntry,
} from '@/hooks/useSequencerProjects'
import { useSequencerStore } from '@/stores/sequencerStore'

interface ProjectLibraryProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Browse every project the user has access to — owned projects plus
 * projects that other users have shared with them. Clicking a row
 * selects the project, which lights it up in ProjectBar and loads it
 * into the workspace.
 *
 * Shared projects already arrive in the Zustand store via the mount-
 * time hydrate in `useSyncSequencerProjects`. This dialog is the view
 * that exposes them with their owner email + role so the user can
 * tell "mine" from "someone shared this with me" at a glance.
 *
 * The refresh button re-invalidates the list query — useful when a
 * share arrives after the page was already mounted and the one-shot
 * hydrate missed it.
 */
export const ProjectLibrary: React.FC<ProjectLibraryProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    data: entries = [],
    isLoading,
    refetch,
  } = useSequencerProjects(isOpen)
  const qc = useQueryClient()

  const currentProjectId = useSequencerStore(s => s.currentProjectId)
  const selectProject = useSequencerStore(s => s.selectProject)
  const projectsInStore = useSequencerStore(s => s.projects)

  const { owned, shared } = useMemo(() => {
    const owned: ProjectListEntry[] = []
    const shared: ProjectListEntry[] = []
    for (const e of entries) {
      if (e.role === 'owner') owned.push(e)
      else shared.push(e)
    }
    const byName = (a: ProjectListEntry, b: ProjectListEntry) =>
      a.name.localeCompare(b.name, undefined, { numeric: true })
    return { owned: owned.sort(byName), shared: shared.sort(byName) }
  }, [entries])

  const handleOpen = (entry: ProjectListEntry) => {
    // The hydrate in useSyncSequencerProjects is one-shot — if a
    // share landed after mount, the project is in the list query
    // but not yet in the Zustand store. Invalidate the queries and
    // let the sync hook pick it up; meanwhile we still close the
    // dialog and select the id so the UI flips as soon as the
    // project lands in the store.
    if (!projectsInStore[entry.id]) {
      qc.invalidateQueries({ queryKey: ['sequencerProjects'] })
      qc.invalidateQueries({ queryKey: ['sequencerProject', entry.id] })
    }
    selectProject(entry.id)
    onClose()
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg max-h-[85vh] bg-surface-raised rounded-xl shadow-high z-50 flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-brd-subtle">
            <div>
              <Dialog.Title className="text-lg font-semibold text-content-primary">
                Pattern library
              </Dialog.Title>
              <Dialog.Description className="text-xs text-content-tertiary mt-0.5">
                Your saved patterns and anything shared with you.
              </Dialog.Description>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => refetch()}
                className="p-2 rounded-md text-content-secondary hover:text-content-primary hover:bg-surface-sunken transition-colors duration-150"
                title="Refresh"
                aria-label="Refresh list"
              >
                <Refresh size={14} />
              </button>
              <Dialog.Close asChild>
                <button
                  className="p-2 rounded-md text-content-secondary hover:text-content-primary hover:bg-surface-sunken transition-colors duration-150"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </Dialog.Close>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {isLoading && entries.length === 0 ? (
              <div className="text-xs text-content-tertiary">Loading…</div>
            ) : entries.length === 0 ? (
              <div className="text-sm text-content-tertiary italic">
                You don&apos;t have any saved patterns yet. Create one in the
                sequencer and it&apos;ll appear here on your next sign-in.
              </div>
            ) : (
              <>
                <Section title="My patterns">
                  {owned.length === 0 ? (
                    <EmptyRow>
                      No patterns yet — the first one you save shows up here.
                    </EmptyRow>
                  ) : (
                    owned.map(entry => (
                      <ProjectRow
                        key={entry.id}
                        entry={entry}
                        active={entry.id === currentProjectId}
                        onClick={() => handleOpen(entry)}
                      />
                    ))
                  )}
                </Section>

                <Section title="Shared with me">
                  {shared.length === 0 ? (
                    <EmptyRow>
                      When someone shares a pattern with you, it&apos;ll show up
                      here.
                    </EmptyRow>
                  ) : (
                    shared.map(entry => (
                      <ProjectRow
                        key={entry.id}
                        entry={entry}
                        active={entry.id === currentProjectId}
                        onClick={() => handleOpen(entry)}
                      />
                    ))
                  )}
                </Section>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const Section: React.FC<{
  title: string
  children: React.ReactNode
}> = ({ title, children }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-content-tertiary mb-2">
      {title}
    </div>
    <div className="space-y-1">{children}</div>
  </div>
)

const EmptyRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-xs text-content-tertiary italic px-3 py-2 rounded-md bg-surface-sunken">
    {children}
  </div>
)

const ProjectRow: React.FC<{
  entry: ProjectListEntry
  active: boolean
  onClick: () => void
}> = ({ entry, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left px-3 py-2 rounded-md border transition-colors duration-150 flex items-center justify-between gap-3 ${
      active
        ? 'border-[color:var(--accent-halo)] bg-accent-muted'
        : 'border-brd-subtle bg-surface-sunken hover:border-brd-emphasis hover:bg-surface-raised'
    }`}
    aria-pressed={active}
  >
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium text-content-primary truncate flex items-center gap-2">
        <span className="truncate">{entry.name}</span>
        {entry.role !== 'owner' && <RoleBadge role={entry.role} />}
      </div>
      <div className="text-[11px] text-content-tertiary truncate">
        {entry.kitId} · {entry.bpm} BPM · {entry.stepCount} steps
        {entry.role !== 'owner' && entry.ownerEmail && (
          <> · shared by {entry.ownerEmail}</>
        )}
      </div>
    </div>
    <div className="text-[10px] text-content-tertiary">
      {formatRelativeTime(entry.updatedAt)}
    </div>
  </button>
)

const RoleBadge: React.FC<{ role: 'editor' | 'viewer' }> = ({ role }) => (
  <span
    className={`flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
      role === 'editor'
        ? 'bg-[color:color-mix(in_srgb,var(--accent)_20%,transparent)] text-accent'
        : 'bg-surface-raised text-content-tertiary border border-brd-subtle'
    }`}
  >
    {role}
  </span>
)

function formatRelativeTime(iso: string): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`
  const days = Math.floor(diffSec / 86400)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}
